import React, { useState, useEffect, useRef, } from 'react';
import { FormGroup, Label, InputGroup, Input, Button, Modal, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { isEmpty, sortBy, sum, groupBy, get, keyBy, omit, mapValues, chunk, } from 'lodash';
import { useDebounce, useToggle, useList, } from 'react-use';
import { toast, } from 'react-toastify';

import firebase from '../../firebase';
import { getDocumentData, batch as batchWrite, } from '../../shared/firebase';
import { isMobile, safePrefix, } from '../../util';
import useFormState from '../hooks/useFormState';
import ProductSelectOptionLabel from '../ProductSelectOptionLabel';
import Field from '../Field';
import { idWithTenant, } from '../../shared/util';
import ListForm from '../ListForm';
import ProgressButton from '../ProgressButton';
import PendingItemForm from '../forms/PendingItemForm';
import useCollectionFetch from '../hooks/useCollectionFetch';
import useDocumentSubscription from '../hooks/useDocumentSubscription';

const { entries, } = Object;
const db = firebase.firestore();
const inventoriesRef = db.collection('inventories');
const tenantsRef = db.collection('tenants');

export default function BatchRevertingShelfModal(props) {
  const { isOpen, tenants, locations, onClickClose, } = props;
  const locationsByKey = keyBy(locations, 'sortKey');
  const tenantsById = keyBy(tenants, 'id');
  const [locationKeyValue, setLocationKeyValue] = useState('');
  const matchedLocation = locationsByKey[locationKeyValue];
  const [productBarcodeInfoValue, setProductBarcodeInfoValue] = useState('');
  const [debouncedProductBarcodeInfoValue, setDebouncedProductBarcodeInfoValue] = useState('');
  const matchedInventories = useCollectionFetch(matchedLocation && !isEmpty(debouncedProductBarcodeInfoValue) && inventoriesRef.where('status', '==', 'shelved').where('productBarcodeInfo', '==', safePrefix(debouncedProductBarcodeInfoValue)).where('locationId', '==', matchedLocation.id), [locationKeyValue, debouncedProductBarcodeInfoValue]);
  const matchedProduct = useDocumentSubscription(matchedInventories[0] && tenantsRef.doc(matchedInventories[0].tenantId).collection('products').doc(matchedInventories[0].productId), [matchedInventories]);
  const productIdInput = useRef(null);
  const locationInput = useRef(null);
  const [number, setNumber] = useState(1);
  const revert = async () => {
    if(!window.confirm('本当に在庫移動しますか？')) return;

    const groupedInventories = groupBy(
      sortBy(matchedInventories, _ => _.createdAt.toDate()).slice(0, number)
    , _ => [_.tenantId, _.receivingRequestId || _.combiningRequestId].join('-'));
    try {
      await entries(groupedInventories).reduce(async (x, [_, inventories]) => {
        await x;

        const [{ tenantId, receivingRequestId, isNg, productId, }] = inventories;
        const receivingRequest = receivingRequestId && await getDocumentData(tenantsRef.doc(tenantId).collection('receivingRequests').doc(receivingRequestId));
        const inventoryIdsFieldName = (isNg ? 'ng' : 'good') + 'InventoryIds';
        await batchWrite(db, inventories, (batch, inventory) => {
          batch.update(inventory.ref, {
            locationId: null,
            status: 'received',
          });
        }, 150);
        await batchWrite(db, chunk(inventories.map(_ => _.id), 150), (batch, inventoryIds) => {
          batch.update(tenantsRef.doc(tenantId).collection('products').doc(productId), {
            [inventoryIdsFieldName]: firebase.firestore.FieldValue.arrayRemove(...inventoryIds),
          });
          if(receivingRequest != null) {
            batch.set(receivingRequest.ref, {
              [[inventoryIdsFieldName, productId].join('-')]: firebase.firestore.FieldValue.arrayRemove(...inventoryIds),
              status: 'processing',
            }, { merge: true });
          }
        }, 1);
      }, Promise.resolve());
    } catch(e) {
      console.error(e);
      toast.error(`失敗しました。一部だけ在庫移動された可能性があります。${e.message}`, { autoClose: false });
      return;
    }
    toast.success('在庫移動しました');
    setProductBarcodeInfoValue('');
    setDebouncedProductBarcodeInfoValue('');
    setLocationKeyValue('');
    locationInput.current.focus();
  };
  useDebounce(() => {
    if(locationKeyValue) productIdInput.current.focus();
  }, 300, [locationKeyValue]);
  useDebounce(() => {
    setDebouncedProductBarcodeInfoValue(productBarcodeInfoValue);
  }, 300, [productBarcodeInfoValue]);

  return (
    <Modal isOpen={isOpen}>
      <div className="modal-header">
        <div className="w-100 d-flex justify-content-between">
          <h5 className="modal-title">
            在庫移動
          </h5>
        </div>
      </div>
      <ModalBody>
        <FormGroup>
          <Label>ロケーション</Label>
          <Input innerRef={locationInput} value={locationKeyValue} onChange={_ => setLocationKeyValue(_.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>商品バーコード</Label>
          <Input innerRef={productIdInput} value={productBarcodeInfoValue} onChange={_ => setProductBarcodeInfoValue(_.target.value)} />
        </FormGroup>
        {
          matchedProduct != null && (
            <div className="card mb-2">
              <div className="card-header">
                見つかった商品
              </div>
              <div className="card-body">
                <ProductSelectOptionLabel admin product={matchedProduct} />
              </div>
            </div>
          )
        }
        {
          matchedInventories.length > 0 && (
            <div className="d-flex text-nowrap gap-1">
              <Input className="text-right p-1" value={number} type="number" onChange={_ => setNumber(parseInt(_.target.value, 10))} />
              <ProgressButton label="在庫移動" color="primary" size="sm" className="" process={revert} disabled={number > matchedInventories.length} />
            </div>
          )
        }
      </ModalBody>
      <ModalFooter>
        <Button className="cancel" color="secondary" onClick={onClickClose}>閉じる</Button>
      </ModalFooter>
    </Modal>
  );
};
